import React from "react";
import { fm } from "./lib/analytics";
import { Kpi } from "./Dashboard";
import { downloadCSV, normPhone, dedupeCustomers } from "./lib/export";
import { api } from "./lib/api";
import CustomersSmart from "./CustomersSmart";
import HSegBar from "./components/HSegBar";
import { InfoTip } from "./components/InfoTip";
import { useCustomerFilter } from "./customer_intelligence/CustomerFilterContext";

/**
 * LegacyCustomersHeader — v200.48 (2026-08-13)
 *
 * Header block that preserves every artefact from the legacy
 * «شرائح العملاء» page (source banner · spending banner · archive tag ·
 * hero KPI × 4 · CampaignImpactSection) but sourced from the LIVE
 * pos_sales feed via /api/customers/directory. Consumed by
 * customer_intelligence/CustomerHub.jsx inside the «directory» tab.
 *
 * User rule (2026-08-13):
 *   «أي بيانات تعرضها الصفحة القديمة من db.datasets تُنقل إلى pos_sales
 *    بنفس نمط دليل العملاء».
 * → Every KPI here reads live pos_sales rows, not the archive.
 *
 * 2026-08 (إصلاح «إجمالي العملاء محصور بنافذة 90 يوم») —
 *   الرقم «الرسمي» كان يعرض عدّاد النافذة النشطة (90 يوم) ويصفه كإجمالي
 *   القاعدة، فيُظهر هبوطًا كاذبًا عند أي تقلّب موسمي. الآن بطاقتان منفصلتان:
 *   «القاعدة الكاملة منذ الافتتاح» (كل العمر، بلا نافذة) و«نشطون آخر Nيوم».
 *   كلتاهما تحترمان فلتر الفرع من الشريط المشترك إن وُجد.
 */
export function LegacyCustomersHeader() {
  const filterCtx = useCustomerFilter();
  const branchId = filterCtx?.branchId || "";
  const [dir, setDir] = React.useState({ status: "loading", data: null });
  const [campaigns, setCampaigns] = React.useState([]);
  // Phase 7c: نافذة تحليلية ثابتة 90 يومًا — غير موصولة بتاريخ الشريط المشترك
  // (المسار 1a). قاعدة العملاء الهيرو لا تُقلَّص بنافذة ضيقة. الفرع فقط موصول.
  React.useEffect(() => {
    let alive = true;
    const params = { window: 90 };
    if (branchId) params.branch_id = branchId;
    api.get("/customers/directory", { params })
      .then(({ data }) => alive && setDir({ status: "ok", data }))
      .catch(() => alive && setDir({ status: "error", data: null }));
    api.get("/campaigns")
      .then(({ data }) => alive && setCampaigns(data.items || []))
      .catch(() => {});
    return () => { alive = false; };
  }, [branchId]);
  const isLive = dir.status === "ok" && dir.data;
  const customers = React.useMemo(() => {
    if (!isLive) return [];
    return (dir.data.customers || []).map(c => ({
      phone: c.phone, name: c.name, visits: c.visits,
      totalSpent: c.spent, paid: c.visits, free: 0,
      lastDate: c.last_visit,
    }));
  }, [isLive, dir.data]);
  const totalBase = isLive ? Number(dir.data.total_base ?? customers.length) : 0;
  const activeCount = isLive ? Number(dir.data.active_count ?? customers.length) : 0;
  const seg8p = customers.filter(c => (c.visits || 0) >= 8).length;
  const activeC = customers.filter(c => (c.visits || 0) > 0);
  const avgVisits = activeC.length
    ? activeC.reduce((s, c) => s + (c.visits || 0), 0) / activeC.length : 0;
  const SIXTY_ISO = new Date(Date.now() - 60 * 864e5).toISOString().slice(0, 10);
  const lostCount = customers.filter(c => !c.lastDate || c.lastDate < SIXTY_ISO).length;
  const loyalPct = customers.length ? (seg8p / customers.length) * 100 : 0;
  const lostPct  = customers.length ? (lostCount / customers.length) * 100 : 0;
  const totalSpent  = customers.reduce((s, c) => s + (c.totalSpent || 0), 0);
  const totalVisits = customers.reduce((s, c) => s + (c.visits || 0), 0);
  const avgSpendPerVisit = totalVisits ? totalSpent / totalVisits : 0;
  const heroKpis = [
    { l: "🏛 القاعدة الكاملة منذ الافتتاح",
      v: totalBase.toLocaleString("en-US"),
      badge: "✅ الرقم الرسمي",
      s: branchId ? "كل التاريخ · هذا الفرع · pos_sales" : "كل التاريخ · كل الفروع · pos_sales",
      c: "b",
      testId: "kpi-total-base-since-opening" },
    { l: "🟢 مجتمع القياس (آخر 90 يوم)",
      v: activeCount.toLocaleString("en-US"),
      s: `${activeCount.toLocaleString("en-US")} من ${totalBase.toLocaleString("en-US")} · مجتمع القياس · pos_sales`,
      c: "g",
      testId: "kpi-active-customers-window" },
    { l: "✨ عملاء أوفياء",
      v: seg8p.toLocaleString("en-US"),
      s: `${loyalPct.toFixed(1)}% من القاعدة · 8+ زيارة`,
      c: loyalPct >= 5 ? "g" : loyalPct >= 2 ? "" : "o" },
    { l: "📊 متوسط الزيارات",
      v: avgVisits.toFixed(1),
      s: "زيارة/عميل نشط · pos_sales",
      c: avgVisits >= 3 ? "g" : avgVisits >= 1.5 ? "" : "o" },
    { l: "🧾 متوسط إنفاق/زيارة",
      v: avgSpendPerVisit.toFixed(1) + " ر",
      s: `الهدف ≥ 14 ر · إجمالي ${fm(Math.round(totalSpent))} ر · ${lostCount} فاقد`,
      c: avgSpendPerVisit >= 14 ? "g" : avgSpendPerVisit >= 10 ? "o" : "r" },
  ];
  return (
    <div data-testid="legacy-customers-header">
      <div className="al alg" data-testid="customers-source-banner"
           style={{ marginBottom: 14, fontSize: 12.5, lineHeight: 1.7 }}>
        🛰 <b>مصدر التحليل:</b>{" "}
        {isLive
          ? `pos_sales · نافذة ${dir.data.window_days}ي · وضع ${dir.data.mode} · تغطية ${dir.data.coverage?.with_key || 0}/${dir.data.coverage?.total_rows || 0}`
          : dir.status === "loading" ? "جارٍ التحميل من pos_sales…"
          : "تعذّر الاتصال بـ pos_sales"}
      </div>
      {/* v200.49 (2026-08-13) — archive tag REMOVED: no number on this
           header comes from db.datasets; the previous badge was a
           misleading source label. Every KPI here is pos_sales. */}
      <Kpi items={heroKpis} />
      <CampaignImpactSection dedupedAll={customers} />
    </div>
  );
}

// Render a customer card row (top-10 lists). Kept at module level so React doesn't
// recreate the component on every Customers render.
function CustomerVisitRow({ c, idx, vcol, spendingEnabled }) {
  return (
    <div className="ti">
      <div className={`trk ${idx < 3 ? (idx === 0 ? "" : "b") : "s"}`}>{idx + 1}</div>
      <div style={{ flex: 1 }}>
        <div className="tn">{c.name}</div>
        <div className="tsb">{c.phone}</div>
      </div>
      <div style={{ textAlign: "left" }}>
        <div className="tv" style={{ color: vcol }}>{c.visits}</div>
        <div className="tsb">زيارة</div>
        {spendingEnabled && (c.totalSpent || 0) > 0 && (
          <div className="tsb" style={{ color: "var(--success)", fontWeight: 600 }}>
            {fm(Math.round(c.totalSpent))} ر
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerSpendRow({ c, idx }) {
  return (
    <div className="ti">
      <div className={`trk ${idx < 3 ? (idx === 0 ? "" : "b") : "s"}`}>{idx + 1}</div>
      <div style={{ flex: 1 }}>
        <div className="tn">{c.name}</div>
        <div className="tsb">{c.phone} · {c.visits} زيارة</div>
      </div>
      <div style={{ textAlign: "left" }}>
        <div className="tv" style={{ color: "var(--success)" }}>{fm(Math.round(c.totalSpent))}</div>
        <div className="tsb">ر</div>
      </div>
    </div>
  );
}

function TopVisitsList({ items, vcol, spendingEnabled }) {
  return (
    <>{items.slice(0, 10).map((c, i) => (
      <CustomerVisitRow key={(c.phone || c.name) + i} c={c} idx={i} vcol={vcol} spendingEnabled={spendingEnabled} />
    ))}</>
  );
}

function TopSpendList({ items }) {
  return (
    <>{items.slice(0, 10).map((c, i) => (
      <CustomerSpendRow key={(c.phone || c.name) + i} c={c} idx={i} />
    ))}</>
  );
}

  // Export-segment button: stable module-level component so React preserves DOM/state across renders.
function ExportBtn({ items, name, label = "تصدير", testId, onExport }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onExport(items, name)}
      disabled={!items.length}
      className="btn btn-soft"
      style={{ fontSize: 11.5, padding: "5px 10px", marginInlineStart: 8 }}
 
     aria-label={`تصدير ${items.length.toLocaleString("en-US")} عميل إلى Excel/CSV`}>
      📥 {label} ({items.length.toLocaleString("en-US")})
    </button>
  );
}

// Module-level "30 days ago" — re-computed on app load. Good enough for a 30-day window.
const THIRTY_DAYS_AGO_ISO = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);

const EMPTY_LIST_DIV = <div style={{ padding: 16, textAlign: "center", color: "var(--muted)" }}>لا يوجد</div>;

// Build a customer list from INVOICES keyed by phone.
// Used ONLY when no customer file is uploaded. visits = invoice count for that phone.
// (The customer file is always preferred when available — its `الزيارات` column is the source of truth.)
export function buildCustomersFromInvoices(invoices) {
  const m = new Map();
  for (const r of invoices || []) {
    const ph = normPhone(r.phone);
    if (!ph) continue; // skip invoices missing phone — they can't be uniquely linked
    let c = m.get(ph);
    if (!c) {
      c = { phone: ph, name: r.client || "", visits: 0, paid: 0, free: 0, is59: false, is59x: false, totalSpent: 0, names: new Map() };
      m.set(ph, c);
    }
    c.visits += 1;
    if (r.isPaid) c.paid += 1;
    if (r.isFree) c.free += 1;
    if (r.is59) c.is59 = true;
    if (r.is59x) c.is59x = true;
    c.totalSpent += Number(r.total) || 0;
    const nm = (r.client || "").trim();
    if (nm) c.names.set(nm, (c.names.get(nm) || 0) + 1);
  }
  // Pick the most-frequent name for each phone
  return Array.from(m.values()).map((c) => {
    let best = "", bestCount = 0;
    for (const [n, count] of c.names) {
      if (count > bestCount) { best = n; bestCount = count; }
    }
    return { phone: c.phone, name: best || c.name || "(بدون اسم)", visits: c.visits, paid: c.paid, free: c.free, is59: c.is59 && !c.is59x, is59x: c.is59x, totalSpent: c.totalSpent };
  }).sort((a, b) => b.visits - a.visits);
}

// PRIMARY strategy: customer file IS the source of truth for visit counts.
// The `الزيارات` column from Retm's customer export already has the correct count
// per phone (Retm computed it internally, by phone, on their side). We do NOT recompute
// visits from invoices — invoices often lack phone column entirely, so any local
// recomputation collapses different customers with the same name (e.g. multiple "محمد")
// into one inflated row.
//
// SPENDING attribution is harder because invoices from Retm currently lack the phone
// column (0/11383 invoices have phones in production data). Our rules:
//   1) If the customer-file row's PHONE matches an invoice phone → exact attribution.
//   2) Else if the customer's NAME is UNIQUE in the file AND matches a single name in
//      invoices → attribute the full invoice-name aggregate to this row.
//   3) Else (name is duplicated in the customer file — 58 different "محمد 59"s) → we
//      CANNOT honestly say which "محمد 59" earned which invoice. Set spending to 0 and
//      mark `_ambiguousSpending = true` so the UI can warn the user and never display
//      a fake number. This is the previous bug fix: before, every duplicate row got the
//      same inflated total (sum of all "محمد 59" invoices), making the data unusable.
export function buildFromCustomerFile(customerFile, invoices) {
  if (!customerFile?.length) return [];

  const deduped = dedupeCustomers(customerFile);

  // Count name occurrences in the customer file (after dedup) so we know which names
  // are unique and therefore safe to attribute by name.
  const nameCountInFile = new Map();
  for (const c of deduped) {
    const nm = (c.name || "").trim().toLowerCase();
    if (nm) nameCountInFile.set(nm, (nameCountInFile.get(nm) || 0) + 1);
  }

  // Aggregate invoices by phone and by name (separately).
  const invByPhone = new Map();
  const invByName = new Map();
  for (const r of invoices || []) {
    const ph = normPhone(r.phone);
    const nm = (r.client || "").trim().toLowerCase();
    if (ph) {
      let s = invByPhone.get(ph);
      if (!s) { s = { paid: 0, free: 0, totalSpent: 0, lastDate: "" }; invByPhone.set(ph, s); }
      if (r.isPaid) s.paid++;
      if (r.isFree) s.free++;
      s.totalSpent += Number(r.total) || 0;
      if ((r.date || "") > s.lastDate) s.lastDate = r.date || "";
    }
    if (nm) {
      let s = invByName.get(nm);
      if (!s) { s = { paid: 0, free: 0, totalSpent: 0, lastDate: "" }; invByName.set(nm, s); }
      if (r.isPaid) s.paid++;
      if (r.isFree) s.free++;
      s.totalSpent += Number(r.total) || 0;
      if ((r.date || "") > s.lastDate) s.lastDate = r.date || "";
    }
  }

  return deduped.map((c) => {
    const ph = normPhone(c.phone);
    const nm = (c.name || "").trim().toLowerCase();
    const phoneMatch = ph ? invByPhone.get(ph) : null;
    const nameIsUnique = nm && nameCountInFile.get(nm) === 1;
    const nameMatch = (nameIsUnique && invByName.get(nm)) || null;
    const inv = phoneMatch || nameMatch;
    const ambiguousSpending = !phoneMatch && !!nm && !nameIsUnique && invByName.has(nm);
    return {
      phone: ph || c.phone || "",
      name: c.name,
      visits: Number(c.visits) || 0,
      is59: !!c.is59 && !c.is59x,
      is59x: !!c.is59x,
      paid: inv ? inv.paid : 0,
      free: inv ? inv.free : 0,
      totalSpent: inv ? inv.totalSpent : 0,
      lastDate: inv ? inv.lastDate : "",
      _hasInvoices: !!inv,
      _matchedBy: phoneMatch ? "phone" : (nameMatch ? "name" : null),
      _ambiguousSpending: ambiguousSpending,
    };
  }).sort((a, b) => b.visits - a.visits);
}

// ======================= CAMPAIGN IMPACT (in Customers page) =======================
// v200.48 — exported so CustomerHub can host it inside «directory».
export function CampaignImpactSection({ dedupedAll }) {
  const [campaigns, setCampaigns] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState("");
  const [impact, setImpact] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    api.get("/campaigns").then(({ data }) => {
      const list = (data.items || []).filter((c) => c.status !== "archived");
      setCampaigns(list);
      if (list.length === 1) setSelectedId(list[0].id);
    }).catch(() => setCampaigns([]));
  }, []);

  const fetchImpact = React.useCallback(async (id) => {
    if (!id) { setImpact(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${id}/impact`);
      setImpact(data);
    } catch { setImpact(null); }
    setLoading(false);
  }, []);

  React.useEffect(() => { fetchImpact(selectedId); }, [selectedId, fetchImpact]);

  // Build a quick lookup of customer file rows by phone — to enrich impact data
  const phoneToCustomer = React.useMemo(() => {
    const m = new Map();
    (dedupedAll || []).forEach((c) => { if (c.phone) m.set(normPhone(c.phone), c); });
    return m;
  }, [dedupedAll]);

  const exportImpact = (kind) => {
    if (!impact) return;
    const rows = (kind === "continued" ? impact.continued : impact.churned).map((r) => {
      const cust = phoneToCustomer.get(normPhone(r.phone));
      const cls = cust?.is59x ? "59X" : cust?.is59 ? "59" : "عادي";
      return [r.name, r.phone, cls, r.during_visits, r.during_spend, r.after_visits, r.after_spend, r.last_visit || "—"];
    });
    downloadCSV(`تأثير_${impact.campaign.name}_${kind}`,
      ["الاسم", "الجوال", "التصنيف", "زيارات أثناء", "إنفاق أثناء", "زيارات بعد", "إنفاق بعد", "آخر زيارة"], rows);
  };

  if (campaigns.length === 0) return null;

  return (
    <div className="card vibrant-hero" style={{ marginBottom: 14 }} data-testid="customer-campaign-impact">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <div>
          <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>🎯 تأثير الحملات على عملائنا</h4>
          <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--muted)" }}>
            اختر حملة لمعرفة من استمر بعدها ومن توقف — مرتبط مباشرةً ببياناتنا.
          </p>
        </div>
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
                data-testid="select-campaign-impact" style={{ minWidth: 240 }}>
          <option value="">— اختر حملة —</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.start_date} → {c.end_date}) {c.status === "active" ? "🟢" : "⏹"}
            </option>
          ))}
        </select>
      </div>

      {loading && <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>⏳ جاري الحساب...</div>}

      {impact && (
        <>
          {/* Methodology footnote — explains exactly how we count "continued"
              so the manager understands the basis of the number. The
              "participation tag" model (59 / 59 X added to customer name
              during the campaign) is the definitive source of truth. */}
          <div className="muted" style={{ marginTop: 8, fontSize: 11, lineHeight: 1.7, padding: "8px 12px", background: "rgba(212,175,55,0.08)", borderRadius: 6 }}>
            <b>📐 منهجية الاحتساب:</b> «المشارك» = العميل المُعلَّم بـ <b>«59»</b> أو <b>«59 X»</b> في اسمه (هذه التعليمات أضفتها أنت يدوياً خلال الحملة، فهي البرهان الرسمي على المشاركة).
            «استمر» = شارك وعنده زيارة بعد <b>{impact.campaign.end_date}</b>.
            «توقف» = شارك ولم يزر بعد ذلك التاريخ.
            {impact.totals.participants_with_window_visit > 0 && (
              <> · <span style={{ color: "var(--brown-deep)" }}>تأكيد إضافي: <b>{fm(impact.totals.participants_with_window_visit)}</b> منهم لديهم فواتير موثقة داخل نافذة الحملة.</span></>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginTop: 8 }}>
            <div className="card" style={{ padding: 8, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "var(--info)" }}>{fm(impact.totals.targeted)}</div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>عدد المستهدفين</div>
              <div style={{ fontSize: 9.5, color: "var(--brown)", marginTop: 2 }}>
                كلهم بعلامة 59 أو 59 X
              </div>
            </div>
            <div className="card" style={{ padding: 8, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "var(--success)" }}>{fm(impact.totals.continued)}</div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>✅ استمروا بعد الحملة</div>
            </div>
            <div className="card" style={{ padding: 8, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "var(--danger)" }}>{fm(impact.totals.churned)}</div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>❌ توقفوا</div>
            </div>
            <div className="card" style={{ padding: 8, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: impact.totals.retention_pct >= 50 ? "var(--success)" : "var(--warn)" }}>
                {impact.totals.retention_pct}%
              </div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>📊 نسبة الاستمرار</div>
            </div>
            <div className="card" style={{ padding: 8, textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--brown-deep)" }}>{fm(impact.totals.total_post_spend)} ر</div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>💰 إنفاقهم بعد الحملة</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--success)", color: "#fff", padding: "5px 10px", borderRadius: 4, marginBottom: 5 }}>
                <span>✅ استمروا ({fm(impact.continued.length)})</span>
                <button onClick={() => exportImpact("continued")} className="btn btn-soft" style={{ fontSize: 10, padding: "2px 8px" }} data-testid="export-continued">📥</button>
              </div>
              <div style={{ maxHeight: 200, overflowY: "auto", fontSize: 12 }}>
                {impact.continued.slice(0, 25).map((r, i) => {
                  const cust = phoneToCustomer.get(normPhone(r.phone));
                  const cls = cust?.is59x ? "🚫 59X" : cust?.is59 ? "✅ 59" : "👤 عادي";
                  return (
                    <div key={i} style={{ padding: "4px 8px", borderBottom: "1px solid var(--border)" }}>
                      <b>{r.name}</b> <span style={{ fontSize: 10, color: "var(--muted)" }}>{cls}</span>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{r.after_visits} زيارة · {fm(r.after_spend)} ر</div>
                    </div>
                  );
                })}
                {impact.continued.length > 25 && <div style={{ padding: 4, color: "var(--muted)", fontSize: 10 }}>+ {impact.continued.length - 25} غيرهم...</div>}
                {impact.continued.length === 0 && <div style={{ padding: 12, color: "var(--muted)", textAlign: "center", fontSize: 11 }}>لا أحد استمر — راجع الحملة 🤔</div>}
              </div>
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--danger)", color: "#fff", padding: "5px 10px", borderRadius: 4, marginBottom: 5 }}>
                <span>❌ توقفوا ({fm(impact.churned.length)})</span>
                <button onClick={() => exportImpact("churned")} className="btn btn-soft" style={{ fontSize: 10, padding: "2px 8px" }} data-testid="export-churned">📥</button>
              </div>
              <div style={{ maxHeight: 200, overflowY: "auto", fontSize: 12 }}>
                {impact.churned.slice(0, 25).map((r, i) => {
                  const cust = phoneToCustomer.get(normPhone(r.phone));
                  const cls = cust?.is59x ? "🚫 59X" : cust?.is59 ? "✅ 59" : "👤 عادي";
                  return (
                    <div key={i} style={{ padding: "4px 8px", borderBottom: "1px solid var(--border)" }}>
                      <b>{r.name}</b> <span style={{ fontSize: 10, color: "var(--muted)" }}>{cls}</span>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{r.during_visits} زيارة أثناء · {fm(r.during_spend)} ر</div>
                    </div>
                  );
                })}
                {impact.churned.length > 25 && <div style={{ padding: 4, color: "var(--muted)", fontSize: 10 }}>+ {impact.churned.length - 25} غيرهم...</div>}
                {impact.churned.length === 0 && <div style={{ padding: 12, color: "var(--muted)", textAlign: "center", fontSize: 11 }}>كل المستهدفين استمروا 🎉</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ======================= CUSTOMERS =======================
// v200.48 (2026-08-13) — export CampaignImpactSection so CustomerHub
// can host it inside the «directory» sub-tab (merger of the legacy
// «شرائح العملاء» page).
export function Customers({ fil, cst, onUpload }) {
  // Load campaigns for the smart panel (journey timeline + flag rows targeted)
  const [campaigns, setCampaigns] = React.useState([]);
  React.useEffect(() => {
    api.get("/campaigns").then(({ data }) => setCampaigns(data.items || [])).catch(() => {});
  }, []);

  // STRATEGY (priority order):
  //  1) If customer file uploaded → use it as source of truth (visits from file's `الزيارات` column)
  //     and enrich with spending data from invoices (paid/free/totalSpent matched by phone or name).
  //  2) Else (only invoices) → derive customers from invoices by phone (visits = invoice count per phone).
  const fromCustomerFile = React.useMemo(() => buildFromCustomerFile(cst, fil), [cst, fil]);
  const fromInvoices = React.useMemo(() => buildCustomersFromInvoices(fil), [fil]);

  let dedupedAll, sourceLabel;
  if (fromCustomerFile.length > 0) {
    dedupedAll = fromCustomerFile;
    const enriched = fromCustomerFile.filter((c) => c._hasInvoices).length;
    sourceLabel = fil.length > 0
      ? `ملف العملاء (${fromCustomerFile.length.toLocaleString("en-US")} عميل) · لهم فواتير في الفترة: ${enriched.toLocaleString("en-US")}`
      : `ملف العملاء (${fromCustomerFile.length.toLocaleString("en-US")} عميل) — عدد الزيارات من عمود "الزيارات" في الملف`;
  } else if (fromInvoices.length > 0) {
    dedupedAll = fromInvoices;
    sourceLabel = `من الفواتير بالجوال (${fromInvoices.length.toLocaleString("en-US")} عميل) — ارفع ملف العملاء للحصول على عدد زيارات أدق`;
  } else {
    dedupedAll = [];
    sourceLabel = "لا توجد بيانات";
  }

  // Spending attribution feature gate — only enable when ≥80% of invoices have phones.
  const invWithPhone = React.useMemo(
    () => fil.filter((r) => normPhone(r.phone)).length,
    [fil],
  );
  const invPhoneCoverage = fil.length > 0 ? (invWithPhone / fil.length * 100) : 0;
  const spendingEnabled = fil.length > 0 && invPhoneCoverage >= 80;

  if (!dedupedAll.length) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 30 }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>👥</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--brown)", marginBottom: 6 }}>لا توجد بيانات عملاء بعد</div>
        <p style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12, lineHeight: 1.7 }}>
          ارفع <b>تقرير الفواتير</b> الذي يحتوي عمود <b>الجوال</b> — هذا أدق مصدر للزيارات.<br />
          أو ارفع <b>ملف العملاء</b> كبديل.
        </p>
        <button
          data-testid="upload-customers-btn"
          className="btn btn-success"
          style={{ marginTop: 10 }}
          onClick={onUpload}
        >
          👥 رفع ملف العملاء
        </button>
      </div>
    );
  }

  // ── KPI computation (pure marketing — no 59 logic) ──
  // Visit segmentation (RFM-style buckets)
  const seg1 = dedupedAll.filter((c) => (c.visits || 0) === 1).length;
  const seg23 = dedupedAll.filter((c) => (c.visits || 0) >= 2 && (c.visits || 0) <= 3).length;
  const seg47 = dedupedAll.filter((c) => (c.visits || 0) >= 4 && (c.visits || 0) <= 7).length;
  const seg8p = dedupedAll.filter((c) => (c.visits || 0) >= 8).length;
  const segNone = dedupedAll.filter((c) => (c.visits || 0) === 0).length;

  // Lost customers — last visit > 60 days ago (or none)
  const SIXTY_DAYS_AGO_ISO = new Date(Date.now() - 60 * 864e5).toISOString().slice(0, 10);
  const lostCount = dedupedAll.filter((c) => !c.lastDate || c.lastDate < SIXTY_DAYS_AGO_ISO).length;
  const lostPct = dedupedAll.length > 0 ? (lostCount / dedupedAll.length) * 100 : 0;

  // Loyal — 8+ visits (lifetime)
  const loyalCount = seg8p;
  const loyalPct = dedupedAll.length > 0 ? (loyalCount / dedupedAll.length) * 100 : 0;

  // Average lifetime visits per active customer
  const activeCustomers = dedupedAll.filter((c) => (c.visits || 0) > 0);
  const avgVisits = activeCustomers.length > 0
    ? activeCustomers.reduce((s, c) => s + (c.visits || 0), 0) / activeCustomers.length
    : 0;

  // Spending
  const totalSpendAll = spendingEnabled ? dedupedAll.reduce((s, c) => s + (c.totalSpent || 0), 0) : 0;
  const activeSpenders = spendingEnabled ? dedupedAll.filter((c) => (c.totalSpent || 0) > 0) : [];
  const totalVisitsAll = activeSpenders.reduce((s, c) => s + (c.paid || 0) + (c.free || 0), 0);
  const avgSpendPerVisit = totalVisitsAll > 0 ? totalSpendAll / totalVisitsAll : 0;

  // KPI status logic — color washes the WHOLE card
  const heroKpis = [
    {
      l: "👥 إجمالي العملاء",
      v: dedupedAll.length.toLocaleString("en-US"),
      s: `${activeCustomers.length.toLocaleString("en-US")} نشط · مجمَّع على مفتاح الهوية (${dedupedAll.length.toLocaleString("en-US")})`,
      c: "b",
    },
    {
      l: "✨ عملاء أوفياء",
      v: loyalCount.toLocaleString("en-US"),
      s: `${loyalPct.toFixed(1)}% من القاعدة · 8+ زيارة`,
      c: loyalPct >= 5 ? "g" : loyalPct >= 2 ? "" : "o",
    },
    {
      l: "📊 متوسط الزيارات",
      v: avgVisits.toFixed(1),
      s: "زيارة/عميل نشط · مدى الحياة",
      c: avgVisits >= 3 ? "g" : avgVisits >= 1.5 ? "" : "o",
    },
    spendingEnabled
      ? {
          l: "🧾 متوسط إنفاق/زيارة",
          v: avgSpendPerVisit.toFixed(1) + " ر",
          s: `الهدف ≥ 14 ر · إجمالي ${fm(Math.round(totalSpendAll))} ر`,
          c: avgSpendPerVisit >= 14 ? "g" : avgSpendPerVisit >= 10 ? "o" : "r",
        }
      : {
          l: "💤 ضائعون (60+ يوم)",
          v: lostCount.toLocaleString("en-US"),
          s: `${lostPct.toFixed(1)}% من القاعدة · يحتاجون استرجاع`,
          c: lostPct >= 40 ? "r" : lostPct >= 20 ? "o" : "g",
        },
  ];

  // RFM-style visit distribution for HSegBar
  const segments = [
    { label: "💎 8+ زيارة (أوفياء)", value: seg8p,  color: "#10B981" },
    { label: "⭐ 4-7 زيارات",        value: seg47,  color: "#3B82F6" },
    { label: "🔄 2-3 زيارات",        value: seg23,  color: "#C2A97F" },
    { label: "🆕 زيارة واحدة",       value: seg1,   color: "#F59E0B" },
    { label: "💤 لم يزر",            value: segNone, color: "#9CA3AF" },
  ];

  return (
    <>
      {/* Source banner */}
      <div className="al alg" data-testid="customers-source-banner" style={{ marginBottom: 14, fontSize: 12.5, lineHeight: 1.7 }}>
        🛰 <b>مصدر التحليل:</b> {sourceLabel}
      </div>

      {/* Spending feature gate notice */}
      {fil.length > 0 && !spendingEnabled && (
        <div className="al alo" data-testid="spending-disabled-banner" style={{ marginBottom: 14, lineHeight: 1.8, fontSize: 12.5 }}>
          <b>💰 إجمالي صرف العميل:</b> معطّل مؤقتاً.
          <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--brown)" }}>
            لإظهار صرف كل عميل بدقة، نحتاج عمود الجوال في تقرير الفواتير من رتم
            (الحالي: {invWithPhone.toLocaleString("en-US")} من {fil.length.toLocaleString("en-US")} فاتورة فيها جوال).
            بمجرد ما يضيف رتم عمود الجوال ويصبح ≥80% من الفواتير معه جوال، الميزة ستتفعّل تلقائياً.
          </div>
        </div>
      )}

      {/* v200.42 (بند ١) — وسم أرشيف فوق البطاقة القديمة مباشرة.
          الـKpi hero strip يُشتقّ من ملف عملاء مرفوع يدوياً
          (datasets.cust)؛ ما دون CustomersSmart أرقام حيّة من الخادم.
          ممنوع رقمان لنفس السؤال بلا فاصل معلَن. */}
      {dedupedAll?.length > 0 && (
        <div data-testid="customers-hero-archive-tag" style={{
          background: "linear-gradient(90deg, rgba(217,119,6,0.14) 0%, rgba(217,119,6,0.05) 100%)",
          border: "1px solid rgba(217,119,6,0.35)",
          borderRadius: 8, padding: "6px 12px",
          marginBottom: 8, color: "#B45309",
          fontSize: 11.5, fontWeight: 700,
        }}>
          🗄 أرشيف — ملف مرفوع يدوياً · {dedupedAll.length.toLocaleString("en-US")} صف ·
          <span style={{ color: "#78350F", fontWeight: 600, marginInlineStart: 6 }}>
            الأرقام أدناه مصدرها الملف؛ الأرقام الحيّة تحت «إحصائيات الشريحة» فقط.
          </span>
        </div>
      )}
      {/* ⭐ Hero KPI strip (strict 4-column grid, status-washed cards) */}
      <Kpi items={heroKpis} />

      {/* (RFM visit distribution removed — replaced by SaudiMarketInsights inside CustomersSmart per user request) */}

      {/* Campaign impact tracking — cross-linked with the campaigns module */}
      <CampaignImpactSection dedupedAll={dedupedAll} />

      {/* ⭐ Smart customer directory with WhatsApp + segments + AI insights + drill-down */}
      <CustomersSmart customers={dedupedAll} spendingEnabled={spendingEnabled} campaigns={campaigns} />
    </>
  );
}
